import { useEffect, useMemo, useState } from "react";
import { Check, Circle, MoveRight } from "lucide-react";
import {
  ROLE_BY_ID,
  transitionOrigins,
  transitionsFrom,
} from "../data/skillDatabase";

export function SkillGraph() {
  const [currentRole, setCurrentRole] = useState("frontend-engineer");
  const options = useMemo(() => transitionsFrom(currentRole), [currentRole]);
  const [activeTarget, setActiveTarget] = useState(options[0]?.to ?? "");

  useEffect(() => {
    setActiveTarget(options[0]?.to ?? "");
  }, [options]);

  const active = options.find((item) => item.to === activeTarget) ?? options[0];
  const current = ROLE_BY_ID.get(currentRole);
  const target = active ? ROLE_BY_ID.get(active.to) : undefined;

  if (!current || !active || !target) return null;

  return (
    <div className="skill-workbench">
      <div className="skill-workbench__toolbar">
        <div>
          <span className="readout">CURRENT ROLE</span>
          <label className="sr-only" htmlFor="current-role">Choose your current role</label>
          <select
            id="current-role"
            value={currentRole}
            onChange={(event) => setCurrentRole(event.target.value)}
          >
            {transitionOrigins.map((item) => (
              <option key={item.id} value={item.id}>{item.name}</option>
            ))}
          </select>
        </div>
        <p>{current.summary}</p>
      </div>

      <div className="role-map" aria-label={`Career transitions from ${current.name}`}>
        <div className="role-map__source">
          <span className="readout">YOU ARE HERE</span>
          <strong>{current.name}</strong>
          <small>{current.requiredSkills.length} mapped skills</small>
        </div>
        <div className="role-map__connector" aria-hidden="true"><MoveRight size={22} /></div>
        <ul className="role-map__targets" aria-label="Possible transitions">
          {options.map((item) => {
            const optionRole = ROLE_BY_ID.get(item.to);
            if (!optionRole) return null;
            const selected = item.to === activeTarget;
            return (
              <li key={item.to}>
                <button
                  type="button"
                  className={selected ? "is-active" : ""}
                  aria-pressed={selected}
                  onClick={() => setActiveTarget(item.to)}
                >
                  <span>{optionRole.name}</span>
                  <strong>{item.match}% match</strong>
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      <section className="transition-detail" aria-live="polite">
        <header>
          <div>
            <span className="readout">SELECTED PATH</span>
            <h3>{current.name} <MoveRight aria-hidden="true" size={22} /> {target.name}</h3>
          </div>
          <div className="match-gauge" aria-label={`${active.match} percent match`}>
            <strong>{active.match}%</strong>
            <span>match</span>
          </div>
        </header>
        <div className="transition-detail__lists">
          <div>
            <h4>Skills you already have</h4>
            <ul>
              {active.transferable.map((skill) => (
                <li key={skill}><Check aria-hidden="true" size={17} /> {skill}</li>
              ))}
            </ul>
          </div>
          <div>
            <h4>Skills to build</h4>
            <ul>
              {active.missing.map((skill) => (
                <li key={skill}><Circle aria-hidden="true" size={16} /> {skill}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}
